import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import SmartImage from "./SmartImage";

// ── Default fallback portrait images (interleaved L/R by index) ──────────────
const DEFAULT_PORTRAITS = [
  "https://images.unsplash.com/photo-1531956656798-56686eeef3d4?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  "https://images.unsplash.com/photo-1529636798458-92182e662485?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  "https://images.unsplash.com/photo-1544005313-94ddf0286df2?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  "https://images.unsplash.com/photo-1524638431109-93d95c968f03?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  "https://images.unsplash.com/photo-1516483638261-f4dbaf036963?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
];

// ── Default fallback cake overlay images ──────────────────────────────────────
const DEFAULT_CAKES = [
  "https://images.unsplash.com/photo-1464349095431-e9a21285b5f3?crop=entropy&cs=srgb&fm=jpg&q=85&w=500",
  "https://images.unsplash.com/photo-1604702389032-28cff7432c48?crop=entropy&cs=srgb&fm=jpg&q=85&w=500",
  "https://images.unsplash.com/photo-1629576690723-bb3a22ae7126?crop=entropy&cs=srgb&fm=jpg&q=85&w=500",
  "https://images.unsplash.com/photo-1659401083689-240a4f511a76?crop=entropy&cs=srgb&fm=jpg&q=85&w=500",
  "https://images.unsplash.com/photo-1517427294546-5aa121f68e8a?crop=entropy&cs=srgb&fm=jpg&q=85&w=500",
  "https://images.unsplash.com/photo-1655762755958-cc0e10095c24?crop=entropy&cs=srgb&fm=jpg&q=85&w=500",
  "https://images.unsplash.com/photo-1602663491496-73f07481dbea?crop=entropy&cs=srgb&fm=jpg&q=85&w=500",
  "https://images.unsplash.com/photo-1700448293876-07dca826c161?crop=entropy&cs=srgb&fm=jpg&q=85&w=500",
];

/**
 * Crossfade image panel — stacks all images and transitions opacity to active.
 *
 * NOTE: the outer div handles positioning (uses the passed className/style so the
 * caller controls absolute placement). The inner div is `relative` so it acts as
 * the stacking context for the absolutely-positioned images. Keeping them separate
 * avoids the Tailwind CSS cascade conflict where `.relative` overrides `.absolute`
 * when both appear on the same element (`.relative` is generated later in the sheet).
 */
function FadePanel({ images, activeIdx, className, style }) {
  if (!images || images.length === 0) return null;
  const active = ((activeIdx % images.length) + images.length) % images.length;
  return (
    <div className={className || ""} style={style}>
      <div className="relative w-full h-full overflow-hidden">
        {images.map((src, i) => (
          <SmartImage
            key={i}
            src={src}
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-center"
            style={{
              opacity: i === active ? 1 : 0,
              transition: "opacity 1500ms ease-in-out",
            }}
            loading={i === 0 ? "eager" : "lazy"}
          />
        ))}
      </div>
    </div>
  );
}

export default function Hero() {
  // Start empty rather than with the stock-photo defaults — showing nothing
  // briefly while the real images load looks far better than a flash of
  // unrelated stock photos before the actual site content swaps in.
  const [portraits, setPortraits]           = useState([]);
  const [mobilePortraits, setMobilePortraits] = useState(null); // null = fall back to desktop set
  const [cakeImgs, setCakeImgs]     = useState([]);
  const [tick, setTick]             = useState(0);

  useEffect(() => {
    // Fetch portrait images from site settings (admin-managed)
    api.get("/settings")
      .then((r) => {
        if (Array.isArray(r.data?.hero_images) && r.data.hero_images.length >= 2) {
          setPortraits(r.data.hero_images);
        } else {
          setPortraits(DEFAULT_PORTRAITS);
        }
        if (Array.isArray(r.data?.hero_images_mobile) && r.data.hero_images_mobile.length >= 2) {
          setMobilePortraits(r.data.hero_images_mobile);
        }
      })
      .catch(() => setPortraits(DEFAULT_PORTRAITS));

    // Fetch cake images from cakes collection for overlays
    api.get("/cakes")
      .then((r) => {
        const urls = Array.isArray(r.data) ? r.data.map((c) => c.image_url).filter(Boolean) : [];
        setCakeImgs(urls.length >= 4 ? urls : DEFAULT_CAKES);
      })
      .catch(() => setCakeImgs(DEFAULT_CAKES));

    // Single 4s ticker — every panel advances together, in lockstep.
    const id = setInterval(() => setTick((t) => t + 1), 4000);
    return () => clearInterval(id);
  }, []);

  // Split portrait pool into left / right panels (odd / even index)
  const leftPortraits  = portraits.filter((_, i) => i % 2 === 0);
  const rightPortraits = portraits.filter((_, i) => i % 2 === 1);

  // Mobile uses its own image set if the admin has set one, otherwise falls back to desktop
  const mobilePool          = mobilePortraits || portraits;
  const leftPortraitsMobile  = mobilePool.filter((_, i) => i % 2 === 0);
  const rightPortraitsMobile = mobilePool.filter((_, i) => i % 2 === 1);

  // All panels share the same tick — every image in the hero changes at once,
  // rather than staggering left/right/cake transitions independently.
  const leftIdx  = tick;
  const rightIdx = tick;
  const cakeIdx  = tick;
  const n        = cakeImgs.length;

  return (
    <section
      className="relative w-full mt-16 lg:mt-[8.5rem] bg-white overflow-hidden min-h-[560px] h-[calc(100svh-4rem)] lg:h-[calc(100svh-8.5rem)]"
      data-testid="hero-section"
    >
      {/* ══════════════ MOBILE (< md) ══════════════════════════════════════════ */}
      <div className="md:hidden absolute inset-0 flex">
        <FadePanel images={leftPortraitsMobile}  activeIdx={leftIdx}  className="w-1/2 h-full" />
        <FadePanel images={rightPortraitsMobile} activeIdx={rightIdx} className="w-1/2 h-full" />
      </div>
      {/* Overlay for text legibility */}
      <div className="md:hidden absolute inset-0 bg-black/45" style={{ zIndex: 5 }} />
      <div
        className="md:hidden absolute inset-0 flex flex-col items-center justify-center text-center px-6"
        style={{ zIndex: 10 }}
      >
        <h1
          className="font-display font-black text-white leading-[0.9]"
          style={{ fontSize: "clamp(3rem, 12vw, 5rem)" }}
          data-testid="hero-title"
        >
          Cake<br />Central
        </h1>
        <Link
          to="/gallery"
          className="mt-6 inline-block bg-[#EC008C] text-white text-[11px] font-semibold tracking-[0.35em] uppercase px-8 py-3 hover:bg-[#C4007A] transition-colors"
        >
          EXPLORE
        </Link>
      </div>

      {/* ══════════════ DESKTOP (>= md) ════════════════════════════════════════ */}

      {/* Left portrait — crossfading */}
      <FadePanel
        images={leftPortraits}
        activeIdx={leftIdx}
        className="hidden md:block absolute inset-y-0 left-0"
        style={{ width: "37%" }}
      />

      {/* Right portrait — crossfading, 2.5s offset */}
      <FadePanel
        images={rightPortraits}
        activeIdx={rightIdx}
        className="hidden md:block absolute inset-y-0 right-0"
        style={{ width: "37%" }}
      />

      {/* ── Centre white column: cake · title · EXPLORE · cake (no overlap) ──── */}
      <div
        className="hidden md:flex absolute inset-y-0 flex-col items-center justify-center gap-6 lg:gap-8 bg-white px-4"
        style={{ left: "37%", right: "37%", zIndex: 30 }}
      >
        {/* Top cake — crossfading, contained within the centre column, nudged left */}
        <div className="shadow-lg overflow-hidden w-32 lg:w-44 aspect-square shrink-0 -translate-x-[30px] lg:-translate-x-[44.9px]">
          <FadePanel images={cakeImgs} activeIdx={cakeIdx % n} className="w-full h-full" />
        </div>

        <div className="text-center">
          <h1
            className="font-display font-black text-ink leading-[0.9]"
            style={{ fontSize: "clamp(2.4rem, 4.5vw, 4.75rem)" }}
            data-testid="hero-title"
          >
            Cake<br />Central
          </h1>
          <Link
            to="/gallery"
            className="mt-6 inline-block bg-[#EC008C] text-white text-[11px] font-semibold tracking-[0.35em] uppercase px-8 py-3 hover:bg-[#C4007A] transition-colors"
          >
            EXPLORE
          </Link>
        </div>

        {/* Bottom cake — crossfading, offset index, nudged right */}
        <div className="shadow-lg overflow-hidden w-32 lg:w-44 aspect-square shrink-0 translate-x-[30px] lg:translate-x-[44.9px]">
          <FadePanel images={cakeImgs} activeIdx={(cakeIdx + Math.floor(n / 2)) % n} className="w-full h-full" />
        </div>
      </div>
    </section>
  );
}
